
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ClockIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  ShareIcon,
  BookmarkIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  ArrowLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  PhotoIcon,
  EyeIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import { stripHtml, formatDate, estimateReadingTime } from '../../services/utils';
import LoadingSpinner from '../common/LoadingSpinner';
import CommentForm from '../comments/CommentForm';
import * as api from '../../services/api';

const articleCache = new Map();
const CACHE_DURATION = 10 * 60 * 1000; 

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [replyToComment, setReplyToComment] = useState(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const convertImageToDataUrl = useCallback(async (imageUrl) => {
    try {
      const response = await fetch(imageUrl, { mode: 'cors' });
      const blob = await response.blob();
      
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.log('Impossible de convertir l\'image:', error);
      return null;
    }
  }, []);

  const memoizedData = useMemo(() => {
    if (!article) return {};

    const imageUrl = article.image || article.featuredImage?.url;
    const formattedDate = formatDate(article.date || article.postDate);
    const readingTime = article.readingTime || estimateReadingTime(article.cleanContent || article.postContent || '');
    const title = stripHtml(article.postTitle || article.title );
    const excerpt = stripHtml(article.postExcerpt || article.excerpt || '');
    const url = window.location.href;
    const createViralShareContent = (platform = 'generic') => {
      let content = '';
            const emojis = {
        whatsapp: '',
        telegram: '',
        twitter: '',
        generic: ''
      };
      
      content += `${emojis[platform] || emojis.generic} ${title}\n\n`;
      
      if (excerpt) {
        content += ` "${excerpt}"\n\n`;
      }
      
      content += `👆 CLIQUEZ pour lire l'article complet sur Xibarubambouck.net\n`;
      content += `🔗 ${url}\n\n`;
            content += ``;
      
      const hashTags = article.tags?.slice(0, 3)?.map(tag => `#${tag.name.replace(/\s+/g, '')}`).join(' ') || '';
      if (hashTags) {
        content += `\n${hashTags}`;
      } else {
        content += `\n`;
      }
      
      return content;
    };

    const createTwitterContent = () => {
      const baseContent = `${title}\n\n ${excerpt.substring(0, 60)}...\n\n👆 Lire sur ${url}`;
      const hashTags = `\n\n#XibaruBambouck #Actualité #Sénégal`;
      
      if (baseContent.length + hashTags.length <= 280) {
        return baseContent + hashTags;
      }
      return baseContent;
    };

    const shareData = {
      title: ` ${title}`,
      text: excerpt,
      // url: url
    };

    if (imageUrl && navigator.canShare) {
      try {
        const testShare = {
          ...shareData,
          files: []
        };
        if (navigator.canShare(testShare)) {
          shareData.files = [];
        }
      } catch (e) {
        console.log('Web Share API avec fichiers non supportée');
      }
    }

    const hashTags = article.tags?.slice(0, 3)?.map(tag => tag.name.replace(/\s+/g, '')).join(',') || '';

    const socialShareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(`🔥 ${title} `)}`,
      
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(createTwitterContent())}`,
      
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(` ${title} `)}`,
      
      whatsapp: `https://wa.me/?text=${encodeURIComponent(createViralShareContent('whatsapp'))}`,
      
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(createViralShareContent('telegram'))}`,
      
      email: (() => {
        const emailSubject = ` Ne ratez pas cet article: ${title}`;
        const emailBody = `Salut !\n\nJe viens de lire un article fascinant sur Xibarubambouck.net que je voulais partager avec toi:\n\n ${title}\n\n ${excerpt}\n\n👆 Clique ici pour le lire: ${url}\n\n`;
        return `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      })()
    };

    return { 
      imageUrl, 
      formattedDate, 
      readingTime, 
      shareData, 
      title, 
      excerpt, 
      socialShareUrls,
      createViralShareContent,
      createTwitterContent,
      hashTags
    };
  }, [article]);

  useEffect(() => {
    if (memoizedData.imageUrl) {
      convertImageToDataUrl(memoizedData.imageUrl).then(setImageDataUrl);
    }
  }, [memoizedData.imageUrl, convertImageToDataUrl]);

  const fetchArticleData = useCallback(async () => {
    if (!slug) {
      setError('Slug manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const cacheKey = `article-${slug}`;
      const cached = articleCache.get(cacheKey);

      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setArticle(cached.article);
        setComments(cached.comments || []);
        setLoading(false);
        return;
      }

      const articleData = await api.fetchArticleBySlug(slug);

      if (!articleData) {
        throw new Error('Article non trouvé');
      }

      if (!articleData.image && articleData.featuredImage?.url) {
        articleData.image = articleData.featuredImage.url;
      }

      if (articleData && typeof document !== 'undefined') {
        updateOpenGraphTags(articleData);
      }

      setArticle(articleData);

      setCommentsLoading(true);
      try {
        const commentsData = await api.fetchComments(articleData.ID);
        const commentsArray = commentsData || [];
        setComments(commentsArray);

        articleCache.set(cacheKey, {
          article: articleData,
          comments: commentsArray,
          timestamp: Date.now()
        });
      } catch (commentsError) {
        console.warn('Erreur lors de la récupération des commentaires:', commentsError);
        setComments([]);
      } finally {
        setCommentsLoading(false);
      }

    } catch (err) {
      console.error('Error fetching article:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement de l\'article');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const updateOpenGraphTags = (articleData) => {
    const title = stripHtml(articleData.postTitle || articleData.title || '');
    const description = stripHtml(articleData.postExcerpt || articleData.excerpt || '');
    const imageUrl = articleData.image || articleData.featuredImage?.url;
    const url = window.location.href;
    
    const updateMetaTag = (property, content) => {
      if (!content) return;
      
      let meta = document.querySelector(`meta[property="${property}"]`) || 
                 document.querySelector(`meta[name="${property}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    updateMetaTag('og:title', `${title} | Xibarubambouck.net`);
    updateMetaTag('og:description', `${description} 👆 Cliquez pour lire l'article complet!`);
    updateMetaTag('og:url', url);
    updateMetaTag('og:type', 'article');
    updateMetaTag('og:site_name', 'Xibarubambouck.net - L\'actualité qui compte');
    
    if (imageUrl) {
      updateMetaTag('og:image', imageUrl);
      updateMetaTag('og:image:width', '1200');
      updateMetaTag('og:image:height', '630');
      updateMetaTag('og:image:alt', title);
    }

    updateMetaTag('twitter:card', imageUrl ? 'summary_large_image' : 'summary');
    updateMetaTag('twitter:title', ` ${title}`);
    updateMetaTag('twitter:description', ` ${description} 👆 Lire sur Xibarubambouck.net`);
    updateMetaTag('twitter:site', '@XibaruBambouck');
    if (imageUrl) {
      updateMetaTag('twitter:image', imageUrl);
      updateMetaTag('twitter:image:alt', title);
    }

    updateMetaTag('article:author', articleData.author?.name || 'Xibarubambouck.net');
    updateMetaTag('article:published_time', articleData.date || articleData.postDate);
    updateMetaTag('article:section', articleData.categories?.[0]?.name || 'Actualités');
    
    updateMetaTag('', `${description}`);
  };

  useEffect(() => {
    fetchArticleData();
  }, [fetchArticleData]);

  const handleCommentSubmit = useCallback(async (commentData) => {
    try {
      setCommentSubmitting(true);
      const newComment = await api.submitComment(commentData);
      setComments(prev => [...prev, newComment]);
      setShowCommentForm(false);
      setReplyToComment(null);
      const cacheKey = `article-${slug}`;
      const cached = articleCache.get(cacheKey);
      if (cached) {
        cached.comments = [...(cached.comments || []), newComment];
      }
    } catch (error) {
      console.error('Erreur lors de la soumission du commentaire:', error);
      throw error;
    } finally {
      setCommentSubmitting(false);
    }
  }, [slug]);

  const handleReply = useCallback((comment) => {
    setReplyToComment(comment);
    setShowCommentForm(true);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyToComment(null);
    setShowCommentForm(false);
  }, []);

  const handleNativeShare = useCallback(async () => {
    if (!navigator.share) return false;

    try {
      const sharePayload = {
        title: `${memoizedData.title} | Xibarubambouck.net`,
        text: memoizedData.createViralShareContent('native'),
        url: memoizedData.url
      };

      if (imageDataUrl && navigator.canShare) {
        try {
          const response = await fetch(imageDataUrl);
          const blob = await response.blob();
          const file = new File([blob], 'article-image.jpg', { type: blob.type });
          
          const shareWithFile = {
            ...sharePayload,
            files: [file]
          };
          
          if (navigator.canShare(shareWithFile)) {
            await navigator.share(shareWithFile);
            return true;
          }
        } catch (fileError) {
          console.log('Partage avec image échoué, tentative sans image...');
        }
      }

      await navigator.share(sharePayload);
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.log('Native share failed:', error);
      }
      return false;
    }
  }, [memoizedData, imageDataUrl]);

  const handleShare = useCallback(async () => {
    if (!memoizedData.shareData) return;

    const nativeShareSuccess = await handleNativeShare();
    
    if (nativeShareSuccess) {
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 3000);
      return;
    }

    setShowShareModal(true);
  }, [memoizedData, handleNativeShare]);

  const handleCopyViralContent = useCallback(async () => {
    const viralContent = memoizedData.createViralShareContent('copy');
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(viralContent);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = viralContent;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setShareSuccess(true);
      setShowShareModal(false);
      setTimeout(() => setShareSuccess(false), 3000);
    } catch (error) {
      console.error('Copy failed:', error);
      if (window.prompt) {
        window.prompt('Copiez le contenu ci-dessous:', viralContent);
      } else {
        alert(`Contenu à copier:\n\n${viralContent}`);
      }
    }
  }, [memoizedData]);

  const handleCopyLink = useCallback(async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(memoizedData.url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = memoizedData.url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setShareSuccess(true);
      setShowShareModal(false);
      setTimeout(() => setShareSuccess(false), 3000);
    } catch (error) {
      console.error('Copy failed:', error);
      if (window.prompt) {
        window.prompt('Copiez le lien ci-dessous:', memoizedData.url);
      } else {
        alert(`Lien à copier: ${memoizedData.url}`);
      }
    }
  }, [memoizedData.url]);

  const handleSocialShare = useCallback((platform) => {
    if (memoizedData.socialShareUrls[platform]) {
      const url = memoizedData.socialShareUrls[platform];
      
      const width = 600;
      const height = 700; 
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      window.open(
        url, 
        `share-${platform}`, 
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );
      
      setShowShareModal(false);
    }
  }, [memoizedData.socialShareUrls]);

  const toggleLike = useCallback(() => setLiked(prev => !prev), []);
  const toggleBookmark = useCallback(() => setBookmarked(prev => !prev), []);
  const toggleCommentForm = useCallback(() => setShowCommentForm(prev => !prev), []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600 text-sm sm:text-base">Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-sm sm:max-w-md mx-auto px-4">
          <ExclamationTriangleIcon className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
            {error === 'Article non trouvé' ? 'Article non trouvé' : 'Erreur de chargement'}
          </h2>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">
            {error === 'Article non trouvé'
              ? "L'article demandé n'existe pas ou n'est plus disponible."
              : error || "Une erreur est survenue lors du chargement de l'article."
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-center">
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-gray-700 transition-colors inline-flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>Retour</span>
            </button>
            <Link
              to="/"
              className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center justify-center text-sm sm:text-base"
            >
              Accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {shareSuccess && (
        // <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm sm:text-base flex items-center space-x-2">
          <FireIcon className="w-5 h-5" />
        // </div>
      )}

      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center">
                  <FireIcon className="w-5 h-5 text-white" />
                </div>
              
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 border-b border-gray-200">
              <div className="mb-4 relative">
                {memoizedData.imageUrl ? (
                  <img
                    src={memoizedData.imageUrl}
                    alt={memoizedData.title}
                    className="w-full h-48 object-cover rounded-xl shadow-md"
                  />
                ) : (
                  <div className="">
                    
                  </div>
                )}
               
              </div>
              
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2">
                   {memoizedData.title}
                </h4>
                <p className="text-sm text-gray-600 line-clamp-3 mb-3">
                   {memoizedData.excerpt}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                     {memoizedData.readingTime} min de lecture
                  </span>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                    Xibarubambouck.net
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6 space-y-3">
               
                
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-700 font-medium border border-gray-300"
                >
                  <ShareIcon className="w-5 h-5" />
                  <span>Copier seulement le lien</span>
                </button>
              </div>

              <div className="border-t pt-6">
                <h5 className="text-sm font-bold text-gray-900 mb-4 flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <span>🚀 Partage sur les réseaux</span>
                  </span>
                 
                </h5>
                
  

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-300 font-medium transform hover:scale-105 shadow-md"
                  >
                    <span>📘</span>
                    <span>Facebook</span>
                  </button>

                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-all duration-300 font-medium transform hover:scale-105 shadow-md"
                  >
                    <span>🐦</span>
                    <span>Twitter</span>
                  </button>

                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-700 hover:bg-blue-800 text-white rounded-lg transition-all duration-300 font-medium transform hover:scale-105 shadow-md"
                  >
                    <span>💼</span>
                    <span>LinkedIn</span>
                  </button>

                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all duration-300 font-medium transform hover:scale-105 shadow-md"
                  >
                    <span>📱</span>
                    <span>WhatsApp</span>
                  </button>

                  <button
                    onClick={() => handleShare('telegram')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-300 font-medium transform hover:scale-105 shadow-md"
                  >
                    <span>✈️</span>
                    <span>Telegram</span>
                  </button>

                  <button
                    onClick={() => handleShare('email')}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all duration-300 font-medium transform hover:scale-105 shadow-md"
                  >
                    <span>📧</span>
                    <span>Email</span>
                  </button>
                </div>


              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header avec titre et métadonnées */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb viral */}
            <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6 overflow-x-auto" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-blue-600 transition-colors whitespace-nowrap font-medium">
                 Xibarubambouck.net
              </Link>
              <span className="text-gray-400">/</span>
              {article.categories?.length > 0 && (
                <>
                  <Link
                    to={`/category/${article.categories[0].slug}`}
                    className="hover:text-blue-600 transition-colors whitespace-nowrap font-medium"
                  >
                     {article.categories[0].name}
                  </Link>
                  <span className="text-gray-400">/</span>
                </>
              )}
              <span className="text-gray-400 truncate font-medium" aria-current="page">
                 {stripHtml(article.postTitle)}
              </span>
            </nav>

          
            <h3 className="text-lg sm:text-xl md:text-2xl lg:text-2xl font-bold text-gray-900 mb-6 leading-tight text-center">
              {stripHtml(article.postTitle)}
            </h3>

            {/* Métadonnées avec style amélioré */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="flex items-center space-x-2 bg-blue-50 px-3 sm:px-4 py-2 rounded-full">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
                  <UserIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm text-gray-600">
                  Par <span className="font-semibold text-gray-900">{article.author?.name || 'Xibarubambouck.net'}</span>
                </span>
              </div>

              <div className="flex items-center space-x-2 bg-green-50 px-3 sm:px-4 py-2 rounded-full">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-green-600 to-green-700 rounded-full flex items-center justify-center">
                  <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm text-gray-600 font-medium">
                   {memoizedData.formattedDate}
                </span>
              </div>

              <div className="flex items-center space-x-2 bg-purple-50 px-3 sm:px-4 py-2 rounded-full">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-purple-700 rounded-full flex items-center justify-center">
                  <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm text-gray-600 font-medium">
                   {memoizedData.readingTime} min de lecture
                </span>
              </div>
            </div>

            {/* Boutons d'action avec style viral */}
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              <button
                onClick={toggleLike}
                className={`flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 rounded-xl transition-all duration-300 text-xs sm:text-sm font-bold transform hover:scale-105 ${liked
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
                  : 'bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-600 border-2 border-gray-200 hover:border-red-300'
                  }`}
                aria-pressed={liked}
              >
                {liked ? (
                  <HeartIconSolid className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
                <span> J'adore</span>
              </button>

              <button
                onClick={toggleBookmark}
                className={`flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 rounded-xl transition-all duration-300 text-xs sm:text-sm font-bold transform hover:scale-105 ${bookmarked
                  ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg'
                  : 'bg-gray-50 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 border-2 border-gray-200 hover:border-yellow-300'
                  }`}
                aria-pressed={bookmarked}
              >
                {bookmarked ? (
                  <BookmarkIconSolid className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <BookmarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
                <span> Sauvegarder</span>
              </button>

              <button
                onClick={handleShare}
                className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transition-all duration-300 text-xs sm:text-sm font-bold shadow-lg transform hover:scale-105"
              >
                <ShareIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span> PARTAGER</span>
              </button>
            </div>

            
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          <article className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8 border border-gray-100">
            {memoizedData.imageUrl && (
              <div className="relative">
                <img
                  src={memoizedData.imageUrl}
                  alt={stripHtml(article.postTitle)}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Watermark discret */}
                <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white px-3 py-1 rounded-lg text-xs font-bold">
                   Xibarubambouck.net
                </div>
              </div>
            )}

            <div className="p-8 xl:p-12">
              {article.postExcerpt && (
                <div className="text-xl text-gray-600 mb-10 italic border-l-4 border-blue-600 pl-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-r-lg shadow-sm">
                   {stripHtml(article.postExcerpt)}
                </div>
              )}

              <div
                className="prose prose-lg max-w-none 
                prose-headings:text-gray-900 prose-headings:mb-4 prose-headings:mt-6
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline 
                prose-img:rounded-xl prose-img:shadow-md prose-img:w-full 
                prose-blockquote:border-l-4 prose-blockquote:border-blue-600 prose-blockquote:bg-blue-50/50 
                prose-blockquote:px-6 prose-blockquote:py-4 prose-blockquote:rounded-r-lg prose-blockquote:my-6
                prose-ul:my-4 prose-ol:my-4 prose-li:my-1
                [&>*]:max-w-none"
                dangerouslySetInnerHTML={{ __html: article.postContent }}
              />
              
              {article.tags?.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex items-center space-x-2 mb-4">
                    <TagIcon className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-semibold text-gray-900">🏷️ Tags:</span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {article.tags.map((tag) => (
                      <Link
                        key={tag.id}
                        to={`/tag/${tag.slug}`}
                        className="px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-sm rounded-full hover:from-blue-100 hover:to-blue-200 hover:text-blue-800 transition-all duration-300 font-medium transform hover:scale-105"
                      >
                        #{tag.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>

          <div className="bg-white rounded-2xl shadow-lg p-8 xl:p-12 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <h3 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
                  <ChatBubbleLeftIcon className="w-5 h-5 text-white" />
                </div>
                <span>💬 Commentaires ({comments.length})</span>
              </h3>

              <div className="flex items-center space-x-3">
               

                <button
                  onClick={toggleCommentForm}
                  disabled={commentSubmitting}
                  className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-900 transition-all duration-300 flex items-center space-x-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                >
                  <span>💬 Commenter</span>
                  {showCommentForm ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {showCommentForm && (
              <div className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
                <CommentForm
                  postId={article.ID}
                  onSubmit={handleCommentSubmit}
                  onCancel={handleCancelReply}
                  isVisible={showCommentForm}
                  parentComment={replyToComment}
                  submitting={commentSubmitting}
                  replyTo={replyToComment}
                />
              </div>
            )}

            {/* Liste des commentaires */}

            {commentsLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : comments.length > 0 ? (
              <div className="space-y-6 mt-8">
                {comments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    onReply={handleReply}
                    disabled={commentSubmitting}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <ChatBubbleLeftIcon className="w-20 h-20 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">💬 Aucun commentaire pour le moment.</p>
                <p className="text-sm mb-4">Soyez le premier à laisser un commentaire!</p>
              
              </div>
            )}
          </div>     
        </div>
      </div>
    </div>
  );
};

const CommentItem = React.memo(({ comment, onReply, disabled }) => (
  <div className="border-b border-gray-200 pb-6 last:border-b-0">
    <div className="flex items-start space-x-4">
      <div className="flex-shrink-0">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
          {(comment.author || 'A').charAt(0).toUpperCase()}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-3">
          <span className="font-semibold text-gray-900">👤 {comment.author || 'Lecteur Xibarubambouck'}</span>
          <span className="text-sm text-gray-500">📅 {formatDate(comment.date)}</span>
        </div>
        <div
          className="prose prose-sm max-w-none text-gray-700 mb-4 break-words"
          dangerouslySetInnerHTML={{ __html: comment.content }}
        />
        <div className="flex items-center space-x-4">
          <button
            onClick={() => onReply(comment)}
            disabled={disabled}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            💬 Répondre
          </button>
          <span className="text-xs text-gray-400">•</span>
          <span className="text-xs text-gray-500">
            🔗 Lecteur de <strong>Xibarubambouck.net</strong>
          </span>
        </div>
      </div>
    </div>
  </div>
));

CommentItem.displayName = 'CommentItem';

export default ArticleDetail;
